import { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  SimpleGrid,
  Text,
  VStack,
  Icon,
  Flex,
  Divider,
  HStack,
} from "@chakra-ui/react";
import { useAuth } from "../../Contexts/AuthContext";
import { useLang } from "../../Hooks";
import { getDashboardData } from "../../Services/api";
import {
  FaUser,
  FaGlobe,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaCommentDots,
} from "react-icons/fa";
import { MdCheckCircle, MdCancel } from "react-icons/md";

const WelcomeCard = ({ clientName, subscription, texts }) => {
  const isActive = subscription?.IsActive;
  const dashboardTexts = texts?.dashboard || {};

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      justify="space-between"
      align="center"
      p={6}
      borderRadius="xl"
      boxShadow="lg"
      sx={{
        bgGradient: isActive
          ? "var(--welcome-card-active-gradient)"
          : "var(--welcome-card-inactive-gradient)",
        color: "var(--welcome-card-text)",
      }}
    >
      <Box>
        <Heading size="lg">
          {dashboardTexts.welcomeBack?.replace("{{name}}", clientName || "") ||
            `Welcome back, ${clientName || ""}!`}
        </Heading>
        <Text opacity={0.8} mt={1}>
          {dashboardTexts.businessSnapshot || "Here's your business snapshot."}
        </Text>
      </Box>
      <VStack
        mt={{ base: 4, md: 0 }}
        p={4}
        borderRadius="lg"
        spacing={1}
        sx={{ bg: "var(--welcome-card-overlay-bg)" }}
      >
        <HStack>
          <Icon
            as={isActive ? MdCheckCircle : MdCancel}
            w={6}
            h={6}
            sx={{
              color: isActive
                ? "var(--welcome-card-icon-active)"
                : "var(--welcome-card-icon-inactive)",
            }}
          />
          <Text fontWeight="bold" fontSize="lg">
            {dashboardTexts.subscription || "Subscription"}{" "}
            {isActive
              ? dashboardTexts.subscriptionActive || "Active"
              : dashboardTexts.subscriptionInactive || "Inactive"}
          </Text>
        </HStack>
        <Text fontSize="sm">
          {dashboardTexts.startedOn || "Started on"}: {subscription?.StartedAt}
        </Text>
        <Text fontSize="sm">
          {dashboardTexts.expiresOn || "Expires on"}: {subscription?.ExpiresAt}
        </Text>
      </VStack>
    </Flex>
  );
};

const StatCard = ({ title, value, icon, texts }) => {
  const dashboardTexts = texts?.dashboard || {};

  return (
    <Flex
      p={3}
      borderRadius="xl"
      boxShadow="md"
      align="center"
      justify="space-between"
      sx={{ bg: "var(--dash-stat-card-bg)" }}
    >
      <Box>
        <Text fontSize="sm" sx={{ color: "var(--dash-stat-card-subtext)" }}>
          {dashboardTexts[title] || title}
        </Text>
        <Heading size="xl" mt={1}>
          {value ?? "N/A"}
        </Heading>
      </Box>
      <Icon
        as={icon}
        boxSize={8}
        sx={{ color: "var(--dash-stat-card-icon)" }}
      />
    </Flex>
  );
};

const ClientDetailsCard = ({ client, texts }) => {
  const dashboardTexts = texts?.dashboard || {};

  const DetailItem = ({ icon, label, value }) => (
    <HStack spacing={1} align="center">
      <Icon
        as={icon}
        boxSize={5}
        sx={{ color: "var(--client-details-card-icon)" }}
      />
      <Box>
        <Text fontSize="xs" sx={{ color: "var(--client-details-card-label)" }}>
          {dashboardTexts[label] || label}
        </Text>
        <Text
          fontWeight="semibold"
          sx={{ color: "var(--client-details-card-value)" }}
        >
          {value || ""}
        </Text>
      </Box>
    </HStack>
  );

  return (
    <Box
      p={4}
      borderRadius="xl"
      boxShadow="md"
      sx={{ bg: "var(--client-details-card-bg)" }}
    >
      <Heading size="md" mb={6}>
        {dashboardTexts.yourInformation || "Your Information"}
      </Heading>
      <Divider
        mb={6}
        sx={{ borderColor: "var(--client-details-card-divider)" }}
      />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4} spacingY={3}>
        <DetailItem icon={FaUser} label="name" value={client?.Name} />
        <DetailItem icon={FaEnvelope} label="email" value={client?.Email} />
        <DetailItem
          icon={FaPhone}
          label="phoneNumber"
          value={client?.PhoneNumber}
        />
        <DetailItem icon={FaGlobe} label="country" value={client?.Country} />
        <DetailItem
          icon={FaBuilding}
          label="companyType"
          value={client?.Type}
        />
        <DetailItem
          icon={FaBuilding}
          label="businessField"
          value={client?.Nitch?.NitchName}
        />
      </SimpleGrid>
    </Box>
  );
};

const DashboardHomePage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const { texts } = useLang();

  useEffect(() => {
    setIsLoading(true);
    getDashboardData()
      .then((response) => {
        setDashboardData(response.data?.data);
      })
      .catch((err) => {
        console.error("Dashboard Fetch Error:", err);
        setError(
          err.message ||
            texts?.dashboard?.failedToFetchDashboardData ||
            "Failed to fetch dashboard data.",
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [user, texts]);

  if (isLoading)
    return (
      <Box textAlign="center" mt={20}>
        <Spinner size="xl" />
      </Box>
    );
  if (error)
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  if (!dashboardData)
    return (
      <Text>
        {texts?.dashboard?.noDataAvailable || "No data available to display."}
      </Text>
    );

  const {
    Client: client,
    SubScription: subscription,
    JoinedChats,
  } = dashboardData;

  return (
    <VStack
      spacing={4}
      align="stretch"
      height="calc(100dvh - 3rem)"
      overflowY={"auto"}
    >
      <WelcomeCard
        clientName={client?.Name}
        subscription={subscription}
        // texts={texts}
      />
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
        <StatCard
          title="joinedChats"
          value={JoinedChats}
          icon={FaCommentDots}
          texts={texts}
        />
      </SimpleGrid>
      <ClientDetailsCard client={client} texts={texts} />
    </VStack>
  );
};

export default DashboardHomePage;
